import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { StudentModal } from './AcademyStudents';
import AcademyEnrollmentDetails from './AcademyEnrollmentDetails';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, DetailField, fmtMoney } from './AcademyShared';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function schedulePreview(start, days, levels) {
  if (!start || !days.length || !levels.length || levels.some(l => !Number.isInteger(Number(l.sessions_count)) || Number(l.sessions_count) < 1 || Number(l.sessions_count) > 500)) return [];
  const cursor = new Date(start + 'T12:00:00Z');
  if (Number.isNaN(cursor.getTime())) return [];
  return levels.map(level => {
    let first = ''; let last = '';
    for (let i = 0; i < Number(level.sessions_count); i++) {
      while (!days.includes(cursor.getUTCDay())) cursor.setUTCDate(cursor.getUTCDate() + 1);
      last = cursor.toISOString().slice(0, 10); if (!first) first = last;
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return { start: first, end: last };
  });
}

function endDateFromDuration(startDate, durationWeeks) {
  const weeks = Number(durationWeeks);
  if (!startDate || !Number.isFinite(weeks) || weeks <= 0) return '';
  const end = new Date(`${startDate}T12:00:00Z`);
  if (Number.isNaN(end.getTime())) return '';
  // The start date is day one of the duration.
  end.setUTCDate(end.getUTCDate() + (Math.ceil(weeks) * 7) - 1);
  return end.toISOString().slice(0, 10);
}

const STATUSES = ['active', 'finished', 'pending_settlement', 'closed', 'cancelled'];

export default function AcademyCohorts() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [bootcampTypes, setBootcampTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [instructors, setInstructors] = useState([]);
  const [filters, setFilters] = useState({ search: '', status: '', bootcamp_type_id: '', instructor_id: '', start_from: '', start_to: '', end_from: '', end_to: '' });
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [addingStudent, setAddingStudent] = useState(null);
  const [addingExpense, setAddingExpense] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ per_page: '50' });
      Object.entries(filters).forEach(([key, value]) => { if (value !== '') query.set(key, value); });
      const url = `${API}/companies/${companyId}/academy/cohorts?${query}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, filters]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/academy/bootcamp-types?per_page=200&status=active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(j => setBootcampTypes(Array.isArray(j.data) ? j.data : [])).catch(() => setBootcampTypes([]));
  }, [companyId]);
  useEffect(() => {
    fetch(`${API}/companies/${companyId}/academy/instructors?per_page=200&status=active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(j => setInstructors(Array.isArray(j.data) ? j.data : [])).catch(() => setInstructors([]));
  }, [companyId]);

  const updateFilter = (key, value) => setFilters(current => ({ ...current, [key]: value }));
  const clearFilters = () => setFilters({ search: '', status: '', bootcamp_type_id: '', instructor_id: '', start_from: '', start_to: '', end_from: '', end_to: '' });

  const changeStatus = async (id, newStatus) => {
    if (newStatus === 'closed' && !window.confirm('Close this cohort? This finalizes the cohort.')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/cohorts/${id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Status change failed'); return; }
    fetchList();
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this cohort?')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/cohorts/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📚 Cohorts</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Cohort</button>
      </div>

      <div style={{ overflowX: 'auto', marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.35fr repeat(7, minmax(150px, 1fr)) auto', gap: 10, minWidth: 1280, padding: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 }}>
        <label style={{ fontSize: 11, color: '#64748b' }}>Search<input style={{ ...inputStyle, marginTop: 4 }} placeholder="Code or name..." value={filters.search} onChange={e => updateFilter('search', e.target.value)} /></label>
        <label style={{ fontSize: 11, color: '#64748b' }}>Status<select style={{ ...inputStyle, marginTop: 4 }} value={filters.status} onChange={e => updateFilter('status', e.target.value)}><option value="">All Statuses</option>{STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}</select></label>
        <label style={{ fontSize: 11, color: '#64748b' }}>Bootcamp Type<select style={{ ...inputStyle, marginTop: 4 }} value={filters.bootcamp_type_id} onChange={e => updateFilter('bootcamp_type_id', e.target.value)}><option value="">All Bootcamp Types</option>{bootcampTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}</select></label>
        <label style={{ fontSize: 11, color: '#64748b' }}>Instructor<select style={{ ...inputStyle, marginTop: 4 }} value={filters.instructor_id} onChange={e => updateFilter('instructor_id', e.target.value)}><option value="">All Instructors</option>{instructors.map(instructor => <option key={instructor.id} value={instructor.id}>{instructor.display_name}</option>)}</select></label>
        <label style={{ fontSize: 11, color: '#64748b' }}>Start Date — From<input type="date" style={{ ...inputStyle, marginTop: 4 }} value={filters.start_from} onChange={e => updateFilter('start_from', e.target.value)} /></label>
        <label style={{ fontSize: 11, color: '#64748b' }}>Start Date — To<input type="date" style={{ ...inputStyle, marginTop: 4 }} value={filters.start_to} onChange={e => updateFilter('start_to', e.target.value)} /></label>
        <label style={{ fontSize: 11, color: '#64748b' }}>End Date — From<input type="date" style={{ ...inputStyle, marginTop: 4 }} value={filters.end_from} onChange={e => updateFilter('end_from', e.target.value)} /></label>
        <label style={{ fontSize: 11, color: '#64748b' }}>End Date — To<input type="date" style={{ ...inputStyle, marginTop: 4 }} value={filters.end_to} onChange={e => updateFilter('end_to', e.target.value)} /></label>
        <button style={{ ...btnSecondary, alignSelf: 'end' }} onClick={clearFilters}>Clear</button>
      </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Code', 'Name', 'Price', 'Capacity', 'Enrolled', 'Withdrawn', 'Collected / Collectable', 'Total Expenses', 'P/L', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={11} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={11} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No cohorts</td></tr>}
              {list.map(c => (
                <React.Fragment key={c.id}>
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 600, cursor: 'pointer', color: '#3b82f6' }} onClick={() => setViewing(viewing?.id === c.id ? null : c)}>{c.cohort_code}</td>
                  <td style={tdStyle}>{c.name}</td>
                  <td style={tdStyle}>{fmtMoney(c.price)}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{c.capacity ?? '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{c.enrollments_count ?? 0}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{c.withdrawn_enrollments_count ?? 0}</td>
                  <td style={{ ...tdStyle, color: Number(c.total_collectable || 0) > 0 && Number(c.total_collected || 0) >= Number(c.total_collectable || 0) ? '#15803d' : undefined, fontWeight: Number(c.total_collectable || 0) > 0 && Number(c.total_collected || 0) >= Number(c.total_collectable || 0) ? 600 : undefined }}>{fmtMoney(c.total_collected)} / {fmtMoney(c.total_collectable)}</td>
                  <td style={tdStyle}>{fmtMoney(c.total_expenses)}</td>
                  <td style={{ ...tdStyle, color: Number(c.total_collected || 0) - Number(c.total_expenses || 0) < 0 ? '#dc2626' : '#15803d', fontWeight: 600 }}>{fmtMoney(Number(c.total_collected || 0) - Number(c.total_expenses || 0))}</td>
                  <td style={tdStyle}><Badge value={c.status} /></td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setViewing(viewing?.id === c.id ? null : c)}>{viewing?.id === c.id ? 'Collapse' : 'View'}</button>
                    <button style={{ ...btnSmall, marginRight: 6, color: '#b45309' }} onClick={() => setAddingExpense(c)}>+ Add Expense</button>
                    {c.status === 'active' && Number(c.started_sessions_count || 0) < 4 && <button style={{ ...btnSmall, marginRight: 6, color: '#2563eb' }} onClick={() => setAddingStudent(c)}>+ Add Student</button>}
                    {c.status !== 'closed' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(c)}>Edit</button>}
                    {c.status === 'finished' && <button style={{ ...btnSmall, marginRight: 6, opacity: Number(c.unsettled_enrollments || 0) > 0 ? 0.5 : 1, cursor: Number(c.unsettled_enrollments || 0) > 0 ? 'not-allowed' : 'pointer' }} disabled={Number(c.unsettled_enrollments || 0) > 0} title={Number(c.unsettled_enrollments || 0) > 0 ? 'Settle all fees or withdraw unpaid students before closing.' : 'Close cohort'} onClick={() => changeStatus(c.id, 'closed')}>Close</button>}
                    {c.status === 'active' && Number(c.started_sessions_count || 0) < 4 && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(c.id, 'cancelled')}>Cancel</button>}
                    {c.status !== 'closed' && Number(c.started_sessions_count || 0) < 4 && <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeItem(c.id)}>Delete</button>}
                  </td>
                </tr>
                {viewing?.id === c.id && <CohortDetail companyId={companyId} cohortId={c.id} onClose={() => setViewing(null)} onChanged={fetchList} />}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <CohortModal companyId={companyId} bootcampTypes={bootcampTypes} cohorts={list} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />}
      {addingStudent && <CohortStudentModal companyId={companyId} cohort={addingStudent} onClose={() => setAddingStudent(null)} onSaved={() => { setAddingStudent(null); fetchList(); }} />}
      {addingExpense && <CohortExpenseModal companyId={companyId} cohort={addingExpense} onClose={() => setAddingExpense(null)} onSaved={() => { setAddingExpense(null); fetchList(); }} />}
    </div>
  );
}

function CohortExpenseModal({ companyId, cohort, onClose, onSaved }) {
  const [form, setForm] = useState({ amount: '', reason: '', record_date: new Date().toISOString().slice(0, 10) });
  const [reasons, setReasons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/companies/${companyId}/academy/expense-categories`, { headers: getAuthHeaders() })
      .then(response => response.ok ? response.json() : { data: [] })
      .then(result => { if (!cancelled) setReasons(Array.isArray(result.data) ? result.data : []); })
      .catch(() => { if (!cancelled) setReasons([]); });
    return () => { cancelled = true; };
  }, [companyId]);

  const save = async () => {
    const reason = form.reason.trim();
    if (!form.amount || Number(form.amount) <= 0 || !reason || !form.record_date) {
      setError('Enter an amount, reason, and date.');
      return;
    }
    setSaving(true); setError('');
    try {
      const response = await fetch(`${API}/companies/${companyId}/academy/expenses`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: form.amount, record_date: form.record_date, cohort_id: cohort.id, category: reason, description: reason }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        setError(result.message || 'Could not save this expense.'); setSaving(false); return;
      }
      onSaved();
    } catch (saveError) { console.error(saveError); setError('Could not save this expense.'); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={event => event.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Add Expense</h3>
        <p style={{ marginTop: -8, marginBottom: 16, color: '#64748b', fontSize: 13 }}>{cohort.name}</p>
        {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <FormRow label="Amount *"><input autoFocus type="number" min="0.01" step="0.01" style={inputStyle} value={form.amount} onChange={event => setForm({ ...form, amount: event.target.value })} /></FormRow>
        <FormRow label="Reason *">
          <input style={inputStyle} list="cohort-expense-reasons" placeholder="For example, Zoom" value={form.reason} onChange={event => setForm({ ...form, reason: event.target.value })} />
          <datalist id="cohort-expense-reasons">{reasons.map(reason => <option key={reason} value={reason} />)}</datalist>
        </FormRow>
        <FormRow label="Date *"><input type="date" style={inputStyle} value={form.record_date} onChange={event => setForm({ ...form, record_date: event.target.value })} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Expense'}</button>
        </div>
      </div>
    </div>
  );
}

export function CohortModal({ companyId, bootcampTypes, cohorts = [], item, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    bootcamp_type_id: item.bootcamp_type_id || '', name: item.name || '',
    start_date: item.start_date || '', end_date: item.end_date || '',
    delivery_mode: item.delivery_mode || 'On-site', price: item.price ?? '',
    capacity: item.capacity || '', location: item.location || '', notes: item.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [instructors, setInstructors] = useState([]);
  const [days, setDays] = useState([]);
  const [startTime, setStartTime] = useState('18:00');
  const [endTime, setEndTime] = useState('20:00');
  const [levels, setLevels] = useState([{ name: 'L1', instructor_id: '', sessions_count: '' }]);
  const [showOtherInstructors, setShowOtherInstructors] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(!isNew);
  const selectedBootcampType = bootcampTypes.find(type => String(type.id) === String(form.bootcamp_type_id));
  const requiredSpecialties = (selectedBootcampType?.specialties || []).map(value => String(value).trim().toLowerCase()).filter(Boolean);
  const matchesSpecialty = instructor => !requiredSpecialties.length || (instructor.specializations || []).some(value => requiredSpecialties.includes(String(value).trim().toLowerCase()));
  const matchingInstructors = instructors.filter(matchesSpecialty);
  const otherInstructors = instructors.filter(instructor => !matchesSpecialty(instructor));
  const inheritedPrice = selectedBootcampType?.default_price ?? '';
  const cohortPrice = form.price === '' || form.price === null || form.price === undefined ? inheritedPrice : form.price;
  const preview = schedulePreview(form.start_date, days, levels);
  const scheduleEndDate = preview.length ? preview[preview.length - 1].end : '';
  const endDate = endDateFromDuration(form.start_date, selectedBootcampType?.duration_weeks) || scheduleEndDate || form.end_date || '';
  const cohortCodePreview = (() => {
    if (!selectedBootcampType?.bootcamp_type_code) return 'Select a bootcamp type';
    const prefix = `${selectedBootcampType.bootcamp_type_code}-`;
    const highestNumber = cohorts
      .filter(cohort => String(cohort.bootcamp_type_id) === String(selectedBootcampType.id))
      .map(cohort => {
        const match = String(cohort.cohort_code || '').match(new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\d+)$`));
        return match ? Number(match[1]) : 0;
      })
      .reduce((highest, number) => Math.max(highest, number), 0);
    return `${prefix}${String(highestNumber + 1).padStart(3, '0')}`;
  })();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let page = 1; let last = 1; const all = [];
        do {
          const response = await fetch(`${API}/companies/${companyId}/academy/instructors?status=active&per_page=200&page=${page}`, { headers: getAuthHeaders() });
          if (!response.ok) throw new Error('Unable to load instructors. Reopen the form to try again.');
          const result = await response.json(); all.push(...(result.data || [])); last = result.last_page || 1; page++;
        } while (page <= last);
        if (!cancelled) setInstructors(all);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [companyId, isNew]);
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API}/companies/${companyId}/academy/cohorts/${item.id}`, { headers: getAuthHeaders() });
        if (!response.ok) throw new Error('Unable to load the full cohort details.');
        const result = await response.json();
        const cohort = result.cohort || result;
        if (cancelled) return;
        const cohortFields = { ...cohort };
        delete cohortFields.default_instructor_id;
        setForm(previous => ({ ...previous, ...cohortFields, delivery_mode: cohort.delivery_mode === 'Remote' ? 'Online' : cohort.delivery_mode, code_suffix: '' }));
        setDays(Array.isArray(cohort.schedule_weekdays) ? cohort.schedule_weekdays.map(Number) : []);
        setStartTime(cohort.schedule_start_time || '18:00');
        setEndTime(cohort.schedule_end_time || '20:00');
        setLevels((cohort.levels || []).map(level => ({ ...level, instructor_id: level.instructor_id || '', sessions_count: level.sessions_count ?? '' })) || []);
      } catch (loadError) { if (!cancelled) setError(loadError.message); }
      finally { if (!cancelled) setLoadingDetails(false); }
    })();
    return () => { cancelled = true; };
  }, [companyId, isNew, item.id]);
  const updateLevel = (index, key, value) => setLevels(levels.map((level, i) => i === index ? { ...level, [key]: value } : level));

  const save = async () => {
    const missing = {};
    if (!form.name.trim()) missing.name = 'Enter the cohort name.';
    if (!form.bootcamp_type_id) missing.bootcamp_type_id = 'Choose a bootcamp type.';
    if (!form.start_date) missing.start_date = 'Choose a start date.';
    setFieldErrors(missing);
    if (Object.keys(missing).length) { setError('Please complete the highlighted fields.'); return; }
    setError('');
    if (isNew) {
      const setupIssues = [];
      if (!days.length) setupIssues.push('session days');
      if (!startTime) setupIssues.push('start time');
      if (!endTime) setupIssues.push('end time');
      if (startTime && endTime && endTime <= startTime) setupIssues.push('an end time after the start time');
      levels.forEach((level, index) => {
        const levelName = `Level ${index + 1}`;
        if (!level.name.trim()) setupIssues.push(`${levelName} name`);
        if (!level.instructor_id) setupIssues.push(`${levelName} instructor`);
        if (!Number.isInteger(Number(level.sessions_count)) || Number(level.sessions_count) < 1) setupIssues.push(`${levelName} session count`);
      });
      if (setupIssues.length) { setError(`Please complete: ${setupIssues.join(', ')}.`); return; }
    }
    setSaving(true);
    try {
      const url = isNew ? `${API}/companies/${companyId}/academy/cohorts` : `${API}/companies/${companyId}/academy/cohorts/${item.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, price: cohortPrice, end_date: endDate || null, capacity: form.capacity || null, schedule_weekdays: days, schedule_start_time: startTime, schedule_end_time: endTime, levels, ...(isNew ? { generate_sessions: true } : {}) }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      const saved = await res.json();
      if (saved.warnings?.instructor_conflicts?.length) window.alert(`Cohort saved. ${saved.warnings.instructor_conflicts.length} session(s) overlap another instructor booking. Review the Academy calendar.`);
      onSaved();
    } catch (e) { setError('Unable to save the cohort. Please try again.'); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 'calc(100vw - var(--access-sidebar-space, 0px))', maxWidth: 'calc(100vw - var(--access-sidebar-space, 0px))', height: '100dvh', maxHeight: '100dvh', borderRadius: 0, margin: `0 0 0 var(--access-sidebar-space, 0px)`, padding: 24, overflowY: 'auto', boxSizing: 'border-box' }} onClick={e => e.stopPropagation()}>
        <div style={{ maxWidth: 1500, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>{isNew ? 'New Cohort' : 'Edit Cohort'}</h3>
          <button type="button" aria-label="Close" onClick={onClose} style={{ background: 'none', border: 0, color: '#64748b', fontSize: 24, cursor: 'pointer' }}>×</button>
        </div>
        {loadingDetails && <p style={{ color: '#64748b' }}>Loading the cohort details…</p>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 }}>
        <FormRow label="Bootcamp Type *">
          <select aria-label="Bootcamp Type" style={{ ...inputStyle, borderColor: fieldErrors.bootcamp_type_id ? '#dc2626' : undefined }} value={form.bootcamp_type_id} onChange={e => { const bootcampType = bootcampTypes.find(type => String(type.id) === e.target.value); setForm({ ...form, bootcamp_type_id: e.target.value, price: bootcampType?.default_price ?? '' }); setShowOtherInstructors(false); setFieldErrors(p => ({ ...p, bootcamp_type_id: '' })); }}>
            <option value="">Select bootcamp type</option>
            {bootcampTypes.map(bt => <option key={bt.id} value={bt.id}>{bt.name}</option>)}
          </select>
          {fieldErrors.bootcamp_type_id && <small style={{ color: '#dc2626' }}>{fieldErrors.bootcamp_type_id}</small>}
        </FormRow>
        <FormRow label="Cohort Code">
          <div style={{ ...inputStyle, background: '#f8fafc', color: '#475569' }}>
            {isNew ? cohortCodePreview : item.cohort_code}
          </div>
          <small>This uses the complete bootcamp type code and the next automatic number.</small>
        </FormRow>
        <FormRow label="Name *">
          <input aria-label="Cohort Name" style={{ ...inputStyle, borderColor: fieldErrors.name ? '#dc2626' : undefined }} value={form.name} onChange={e => { setForm({ ...form, name: e.target.value }); setFieldErrors(p => ({ ...p, name: '' })); }} />
          {fieldErrors.name && <small style={{ color: '#dc2626' }}>{fieldErrors.name}</small>}
        </FormRow>
        <FormRow label="Start Date *">
            <input aria-label="Start Date" type="date" style={{ ...inputStyle, borderColor: fieldErrors.start_date ? '#dc2626' : undefined }} value={form.start_date} onChange={e => { setForm({ ...form, start_date: e.target.value }); setFieldErrors(p => ({ ...p, start_date: '' })); }} />
            {fieldErrors.start_date && <small style={{ color: '#dc2626' }}>{fieldErrors.start_date}</small>}
          </FormRow>
          <FormRow label="End Date (automatic)">
            <input aria-label="End Date" readOnly type="date" style={inputStyle} value={endDate} />
            {selectedBootcampType?.duration_weeks ? <small>Calculated from the {selectedBootcampType.duration_weeks}-week bootcamp duration.</small> : <small>Set a duration on the bootcamp type to calculate this date.</small>}
          </FormRow>
          <FormRow label="Delivery Mode">
            <select aria-label="Delivery Mode" style={inputStyle} value={form.delivery_mode} onChange={e => setForm({ ...form, delivery_mode: e.target.value })}>
              <option>On-site</option><option>Online</option><option>Hybrid</option>
            </select>
          </FormRow>
          <FormRow label="Price">
            <input aria-label="Cohort Price" type="number" min="0" step="0.01" style={inputStyle} value={cohortPrice} onChange={e => setForm({ ...form, price: e.target.value })} />
            <small>Starts with the bootcamp type price. You can override it for this cohort.</small>
          </FormRow>
        </div>
        <>
          <FormRow label="Session Days *">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>{DAYS.map((day, index) => (
              <button key={day} type="button" aria-pressed={days.includes(index)} style={{ ...btnSmall, background: days.includes(index) ? '#dbeafe' : '#fff', color: days.includes(index) ? '#1d4ed8' : '#374151' }} onClick={() => setDays(days.includes(index) ? days.filter(d => d !== index) : [...days, index])}>{day}</button>
            ))}</div>
          </FormRow>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormRow label="Start Time *"><input aria-label="Start Time" type="time" style={inputStyle} value={startTime} onChange={e => setStartTime(e.target.value)} /></FormRow>
            <FormRow label="End Time *"><input aria-label="End Time" type="time" style={inputStyle} value={endTime} onChange={e => setEndTime(e.target.value)} /></FormRow>
          </div>
          <h4>Levels *</h4>
          <p style={{ color: '#6b7280' }}>Levels run sequentially on the selected days. Sessions will appear in the Academy calendar and attendance.</p>
          {levels.map((level, index) => <fieldset key={index} style={{ border: '1px solid #d1d5db', borderRadius: 8, marginBottom: 10, padding: 10 }}>
            <legend>Level {index + 1}</legend>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
              <FormRow label="Name"><input aria-label={`Level ${index + 1} name`} style={inputStyle} value={level.name} onChange={e => updateLevel(index, 'name', e.target.value)} /></FormRow>
              <FormRow label="Instructor"><select aria-label={`Level ${index + 1} instructor`} style={inputStyle} value={level.instructor_id} onChange={e => updateLevel(index, 'instructor_id', e.target.value)}><option value="">Select instructor</option><optgroup label={requiredSpecialties.length ? 'Matching specialties' : 'All instructors'}>{matchingInstructors.map(i => <option key={i.id} value={i.id}>{i.display_name}</option>)}</optgroup>{showOtherInstructors && otherInstructors.length > 0 && <optgroup label="Other instructors">{otherInstructors.map(i => <option key={i.id} value={i.id}>{i.display_name}</option>)}</optgroup>}</select>{requiredSpecialties.length > 0 && otherInstructors.length > 0 && <button type="button" style={{ ...btnSmall, marginTop: 5 }} onClick={() => setShowOtherInstructors(value => !value)}>{showOtherInstructors ? 'Hide other instructors' : 'Other instructors'}</button>}</FormRow>
              <FormRow label="Number of Sessions"><input aria-label={`Level ${index + 1} sessions`} type="number" min="1" max="500" step="1" style={inputStyle} value={level.sessions_count} onChange={e => updateLevel(index, 'sessions_count', e.target.value)} /></FormRow>
            </div>
            {preview[index] && <p>{preview[index].start} → {preview[index].end}</p>}
            {levels.length > 1 && <button style={btnSmall} onClick={() => setLevels(levels.filter((_, i) => i !== index))}>Remove Level {index + 1}</button>}
          </fieldset>)}
          <button style={btnSmall} disabled={levels.length >= 50} onClick={() => setLevels([...levels, { name: `L${levels.length + 1}`, instructor_id: '', sessions_count: '' }])}>+ Add Level</button>
          <p style={{ color: '#6b7280' }}>{endDate ? `${levels.reduce((total, l) => total + Number(l.sessions_count), 0)} sessions · Ends ${endDate}` : 'Select a start date, session days and session counts to calculate the end date.'}</p>
        </>
        {error && <p role="alert" style={{ color: '#dc2626' }}>{error}</p>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <FormRow label="Capacity">
            <input type="number" style={inputStyle} value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} />
          </FormRow>
          <FormRow label="Location">
            <input style={inputStyle} value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
          </FormRow>
        </div>
        <FormRow label="Notes">
          <textarea style={{ ...inputStyle, minHeight: 60 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
        </FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
        </div>
      </div>
    </div>
  );
}

function CohortDetail({ companyId, cohortId, onClose, onChanged }) {
  const [cohort, setCohort] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [addingLevel, setAddingLevel] = useState(false);
  const [viewingEnrollment, setViewingEnrollment] = useState(null);
  const [levelForm, setLevelForm] = useState({ name: '', level_order: 1, start_date: '', end_date: '' });

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/academy/cohorts/${cohortId}`, { headers: getAuthHeaders() });
      const j = await res.json();
      setCohort(j.cohort ? { ...j.cohort, totals: j.totals, expenses: j.expenses || [] } : j);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId, cohortId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const addLevel = async () => {
    if (!levelForm.name.trim()) { alert('Level name required'); return; }
    const res = await fetch(`${API}/companies/${companyId}/academy/cohorts/${cohortId}/levels`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(levelForm),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Failed to add level'); return; }
    setAddingLevel(false);
    setLevelForm({ name: '', level_order: 1, start_date: '', end_date: '' });
    fetchDetail();
    onChanged();
  };

  const removeLevel = async (levelId) => {
    if (!window.confirm('Delete this level?')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/cohorts/${cohortId}/levels/${levelId}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete level'); return; }
    fetchDetail();
    onChanged();
  };

  const withdrawStudent = async (enrollment) => {
    const reason = window.prompt(`Withdrawal reason for ${enrollment.student?.full_name || 'this student'}:`);
    if (reason === null) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/enrollments/${enrollment.id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'withdrawn', reason }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Unable to withdraw student'); return; }
    fetchDetail();
    onChanged();
  };

  const waiveInstallment = async (installment) => {
    const reason = window.prompt('Reason for waiving this payment:');
    if (reason === null || !reason.trim()) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/installments/${installment.id}/waive`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Unable to waive payment'); return; }
    fetchDetail(); onChanged();
  };

  return (
    <tr>
      <td colSpan={11} style={{ padding: 0, borderBottom: '1px solid #dbeafe' }}>
      <div style={{ background: '#f8fbff', padding: 20, borderTop: '1px solid #bfdbfe' }}>
        {initialLoading || !cohort ? <div>Loading...</div> : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ marginTop: 0 }}>{cohort.name} <Badge value={cohort.status} /></h3>
              <button style={btnSecondary} onClick={onClose}>Collapse</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: '4px 14px', marginBottom: 8 }}>
              <DetailField label="Code" value={cohort.cohort_code} />
              <DetailField label="Bootcamp Type" value={cohort.bootcamp_type?.name} />
              <DetailField label="Location" value={cohort.location} />
              <DetailField label="Start Date" value={cohort.start_date} />
              <DetailField label="End Date" value={cohort.end_date} />
              <DetailField label="Price" value={fmtMoney(cohort.price)} />
              <DetailField label="Capacity" value={cohort.capacity} />
              <DetailField label="Session Days" value={(cohort.schedule_weekdays || []).map(d => DAYS[d]).join(', ') || '—'} />
              <DetailField label="Session Time" value={cohort.schedule_start_time ? `${cohort.schedule_start_time.slice(0,5)} – ${cohort.schedule_end_time?.slice(0,5) || ''}` : '—'} />
              {cohort.totals && <>
                <DetailField label="Enrollments" value={cohort.totals.enrollments_count} />
                <DetailField label="Total Income" value={fmtMoney(cohort.totals.total_income)} />
                <DetailField label="Total Expense" value={fmtMoney(cohort.totals.total_expense)} />
              </>}
            </div>

            <section style={{ margin: '18px 0 24px' }}>
              <h4 style={{ margin: '0 0 10px' }}>Expense details</h4>
              <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['Date', 'Reference', 'Category', 'Description', 'Status', 'Amount'].map(label => <th key={label} style={thStyle}>{label}</th>)}</tr></thead>
                  <tbody>
                    {(cohort.expenses || []).map(expense => (
                      <tr key={expense.id} style={{ color: expense.status === 'reversed' ? '#94a3b8' : undefined }}>
                        <td style={tdStyle}>{expense.record_date?.slice(0, 10) || '—'}</td>
                        <td style={tdStyle}>{expense.expense_code || '—'}</td>
                        <td style={tdStyle}>{expense.category || '—'}</td>
                        <td style={{ ...tdStyle, whiteSpace: 'normal' }}>{expense.description || '—'}</td>
                        <td style={tdStyle}>{expense.status === 'reversed' ? <span style={{ color: '#dc2626' }}>Reversed</span> : expense.status}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtMoney(expense.amount)}</td>
                      </tr>
                    ))}
                    {!cohort.expenses?.length && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>No expenses recorded for this cohort.</td></tr>}
                  </tbody>
                  <tfoot><tr><td colSpan={5} style={{ ...tdStyle, fontWeight: 600 }}>Total expenses <span style={{ fontWeight: 400, color: '#64748b' }}>(excluding reversed)</span></td><td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(cohort.totals?.total_expense)}</td></tr></tfoot>
                </table>
              </div>
            </section>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, marginBottom: 6 }}>
              <h4 style={{ margin: 0 }}>Levels</h4>
              <button style={btnSmall} onClick={() => setAddingLevel(v => !v)}>{addingLevel ? 'Cancel' : '+ Add Level'}</button>
            </div>
            {addingLevel && (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                  <FormRow label="Name"><input style={inputStyle} value={levelForm.name} onChange={e => setLevelForm({ ...levelForm, name: e.target.value })} /></FormRow>
                  <FormRow label="Order"><input type="number" style={inputStyle} value={levelForm.level_order} onChange={e => setLevelForm({ ...levelForm, level_order: e.target.value })} /></FormRow>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <FormRow label="Start Date"><input type="date" style={inputStyle} value={levelForm.start_date} onChange={e => setLevelForm({ ...levelForm, start_date: e.target.value })} /></FormRow>
                  <FormRow label="End Date"><input type="date" style={inputStyle} value={levelForm.end_date} onChange={e => setLevelForm({ ...levelForm, end_date: e.target.value })} /></FormRow>
                </div>
                <button style={btnPrimary} onClick={addLevel}>Save Level</button>
              </div>
            )}
            <table style={tableStyle}>
              <thead><tr>{['Order', 'Name', 'Instructor', 'Sessions', 'Start', 'End', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {(cohort.levels || []).length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No levels</td></tr>}
                {(cohort.levels || []).map(l => (
                  <tr key={l.id}>
                    <td style={tdStyle}>{l.level_order}</td>
                    <td style={tdStyle}>{l.name}</td>
                    <td style={tdStyle}>{l.instructor?.display_name || '—'}</td>
                    <td style={tdStyle}>{l.sessions_count ?? '—'}</td>
                    <td style={tdStyle}>{l.start_date || '—'}</td>
                    <td style={tdStyle}>{l.end_date || '—'}</td>
                    <td style={tdStyle}><button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeLevel(l.id)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 16, marginBottom: 6 }}><h4 style={{ margin: 0 }}>Enrolled Students</h4></div>
            <table style={tableStyle}>
              <thead><tr>{['Name', 'Total Fees', 'Paid', 'Pending', 'Next Payment', 'Actions'].map(header => <th key={header} style={thStyle}>{header}</th>)}</tr></thead>
              <tbody>
                {(cohort.enrollments || []).length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No enrolled students</td></tr>}
                {(cohort.enrollments || []).map(enrollment => {
                  const totalFees = Number(enrollment.tuition_fee || 0) - Number(enrollment.discount_amount || 0);
                  const installments = enrollment.payment_plan?.installments || [];
                  const paid = (enrollment.payments || []).filter(payment => payment.status === 'completed').reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
                  const pending = enrollment.status === 'withdrawn' ? 0 : Math.max(0, totalFees - paid);
                  const outstandingInstallments = installments.filter(installment => !['paid', 'waived'].includes(installment.status) && Number(installment.amount_due || 0) > Number(installment.amount_paid || 0));
                  const overdueInstallments = outstandingInstallments.filter(installment => installment.due_date < new Date().toISOString().slice(0, 10));
                  const nextPayment = outstandingInstallments.sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)))[0];
                  const overdue = nextPayment && nextPayment.due_date < new Date().toISOString().slice(0, 10);
                  return <tr key={enrollment.id}>
                    <td style={tdStyle}>{enrollment.student?.full_name || '—'}</td>
                    <td style={tdStyle}>{fmtMoney(totalFees)}</td>
                    <td style={tdStyle}>{fmtMoney(paid)}</td>
                    <td style={tdStyle}>{fmtMoney(pending)}</td>
                    <td style={{ ...tdStyle, color: overdue ? '#dc2626' : '#374151', fontWeight: overdue ? 700 : 400 }}>{overdueInstallments.length > 1 ? <>{fmtMoney(overdueInstallments.reduce((sum, installment) => sum + Number(installment.amount_due || 0) - Number(installment.amount_paid || 0), 0))} · {overdueInstallments.length} overdue receipts</> : nextPayment ? <>{fmtMoney(Number(nextPayment.amount_due || 0) - Number(nextPayment.amount_paid || 0))} · {nextPayment.due_date}{overdue ? ' (Overdue)' : ''}</> : '—'}</td>
                    <td style={tdStyle}><button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setViewingEnrollment(enrollment)}>Details</button>{enrollment.status === 'active' ? <>{nextPayment && <button style={{ ...btnSmall, marginRight: 6, color: '#b45309' }} onClick={() => waiveInstallment(nextPayment)}>Waive</button>}{pending > 0 && <button style={{ ...btnSmall, color: '#dc2626' }} onClick={() => withdrawStudent(enrollment)}>Withdraw</button>}</> : <Badge value={enrollment.status} />}</td>
                  </tr>;
                })}
              </tbody>
            </table>
            {viewingEnrollment && <AcademyEnrollmentDetails companyId={companyId} enrollment={viewingEnrollment} onClose={() => setViewingEnrollment(null)} onSaved={async () => { await fetchDetail(); await onChanged(); }} />}
          </>
        )}
      </div>
      </td>
    </tr>
  );
}

function CohortStudentModal({ companyId, cohort, onClose, onSaved }) {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [newStudent, setNewStudent] = useState(false);
  const [saving, setSaving] = useState(false);
  const loadStudents = useCallback(() => fetch(`${API}/companies/${companyId}/academy/students?per_page=500&enrollable=1`, { headers: getAuthHeaders() })
    .then(response => response.ok ? response.json() : { data: [] }).then(data => setStudents(data.data || [])), [companyId]);
  useEffect(() => { loadStudents(); }, [loadStudents]);
  const enroll = async () => {
    if (!studentId) return;
    setSaving(true);
    const response = await fetch(`${API}/companies/${companyId}/academy/enrollments`, { method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ student_id: studentId, cohort_id: cohort.id, tuition_fee: cohort.price }) });
    if (!response.ok) { const data = await response.json().catch(() => ({})); alert(data.message || Object.values(data.errors || {}).flat().join(' ') || 'Could not enroll this student.'); setSaving(false); return; }
    onSaved();
  };
  if (newStudent) return <StudentModal companyId={companyId} item={{}} onClose={() => setNewStudent(false)} onSaved={student => { setNewStudent(false); loadStudents(); setStudentId(String(student.id)); }} />;
  return <div style={overlayStyle} onClick={onClose}><div style={modalStyle} onClick={event => event.stopPropagation()}>
    <h3 style={{ marginTop: 0 }}>Add Student to {cohort.name}</h3>
    <FormRow label="Student"><select style={inputStyle} value={studentId} onChange={event => setStudentId(event.target.value)}><option value="">Select a student</option>{students.map(student => <option key={student.id} value={student.id}>{student.full_name} ({student.student_code})</option>)}</select></FormRow>
    <p>Enrollment amount: {fmtMoney(cohort.price)}</p>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 16 }}><button style={btnSecondary} onClick={() => setNewStudent(true)}>+ New Student</button><div style={{ display: 'flex', gap: 10 }}><button style={btnSecondary} onClick={onClose}>Cancel</button><button style={btnPrimary} disabled={!studentId || saving} onClick={enroll}>{saving ? 'Enrolling...' : 'Enroll Student'}</button></div></div>
  </div></div>;
}
